// src/CrosswordBoard.js
import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';

// --- LOGIC: خوارزمية الكلمات المتقاطعة ---
const generateCrosswordLogic = (wordList) => {
  const gridSize = 20;
  const grid = Array.from({ length: gridSize }, () => Array(gridSize).fill(null));
  const placedWords = [];

  // ترتيب الكلمات من الأطول للأقصر
  const sortedWords = [...wordList].sort((a, b) => b.word.length - a.word.length);

  const canPlace = (word, row, col, dir) => {
    const [dRow, dCol] = dir === 'across' ? [0, 1] : [1, 0];

    if (row < 0 || col < 0) return false;
    if (dir === 'across' && col + word.length > gridSize) return false;
    if (dir === 'down' && row + word.length > gridSize) return false;

    for (let i = 0; i < word.length; i++) {
      const r = row + (i * dRow);
      const c = col + (i * dCol);
      const cell = grid[r][c];

      if (cell !== null && cell.char !== word[i]) return false;
    }
    return true;
  };

  const place = (item, row, col, dir) => {
    const [dRow, dCol] = dir === 'across' ? [0, 1] : [1, 0];
    for (let i = 0; i < item.word.length; i++) {
      const r = row + (i * dRow);
      const c = col + (i * dCol);
      grid[r][c] = { char: item.word[i] };
    }
    placedWords.push({ ...item, row, col, dir, number: placedWords.length + 1 });
  };

  for (const item of sortedWords) {
    const word = item.word;
    let placed = false;

    // أول كلمة فالوسط
    if (placedWords.length === 0) {
      const startRow = Math.floor(gridSize / 2);
      const startCol = Math.floor((gridSize - word.length) / 2);
      place(item, startRow, startCol, 'across');
      continue;
    }

    // باقي الكلمات
    for (const p of placedWords) {
      for (let i = 0; i < word.length; i++) {
        for (let j = 0; j < p.word.length; j++) {
          if (word[i] === p.word[j]) {
            const newDir = p.dir === 'across' ? 'down' : 'across';
            const newRow = p.row + (p.dir === 'down' ? j : 0) - (newDir === 'down' ? i : 0);
            const newCol = p.col + (p.dir === 'across' ? j : 0) - (newDir === 'across' ? i : 0);

            if (canPlace(word, newRow, newCol, newDir)) {
              place(item, newRow, newCol, newDir);
              placed = true;
              break;
            }
          }
        }
        if (placed) break;
      }
      if (placed) break;
    }
  }

  return { grid, words: placedWords };
};

// --- COMPONENT: الواجهة ---
const CrosswordBoard = ({ onAddToBook }) => {
  const [inputText, setInputText] = useState("REACT: Library\nJAVA: Backend\nCSS: Style\nHTML: Web");
  const [puzzle, setPuzzle] = useState(null);
  const [error, setError] = useState(null); // حالة لتسجيل الأخطاء
  const [showSolution, setShowSolution] = useState(false);
  const printRef = useRef();

  const handleGenerate = () => {
    try {
      setError(null);
      const list = inputText.split('\n').map(line => {
        const [w, c] = line.split(':');
        return (w && c) ? { word: w.trim().toUpperCase(), clue: c.trim() } : null;
      }).filter(x => x && x.word.length > 1);

      if (list.length < 2) throw new Error("Please enter at least 2 valid words (WORD: CLUE).");

      const res = generateCrosswordLogic(list);
      setPuzzle(res);
      setShowSolution(false);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const handleAddToBook = () => {
    if (printRef.current) {
      html2canvas(printRef.current).then(canvas => onAddToBook('Crossword', canvas.toDataURL()));
    }
  };

  useEffect(() => { handleGenerate(); }, []);

  // في حالة وجود خطأ جسيم، لا تظهر شاشة بيضاء
  if (error) {
    return (
      <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
        <h3>⚠️ حدث خطأ:</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>إعادة تحميل الصفحة</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
      <h2 style={{ color: '#2c3e50' }}>Crossword Generator 📝</h2>

      <div style={{ background: 'white', padding: '15px', borderRadius: '10px', marginBottom: '20px', width: '100%', maxWidth: '600px' }}>
        <textarea 
          value={inputText} 
          onChange={e => setInputText(e.target.value)} 
          style={{ width: '100%', height: '80px', padding: '10px', border:'1px solid #ddd' }} 
        />
        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          <button onClick={handleGenerate} style={btnStyle('#6c5ce7')}>🔄 Generate</button>
          <button onClick={() => setShowSolution(!showSolution)} style={btnStyle('#00b894')}>
            {showSolution ? 'Hide' : 'Solution'}
          </button>
          <button onClick={handleAddToBook} style={btnStyle('#e67e22')}>📖 Add</button>
        </div>
      </div>

      <div ref={printRef} style={{ background: 'white', padding: '20px', borderRadius: '10px', border: '1px solid #eee' }}>
        {puzzle && puzzle.words.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(20, 20px)', border: '2px solid #333', background: '#333', gap: '1px' }}>
            {puzzle.grid.map((row, r) => row.map((cell, c) => (
              <div key={`${r}-${c}`} style={{ 
                width: '20px', height: '20px', 
                background: cell ? 'white' : '#333', 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: 'bold', position: 'relative'
              }}>
                {cell && showSolution ? cell.char : ''}
                {puzzle.words.some(w => w.row === r && w.col === c) && (
                  <span style={{ position: 'absolute', top: 0, left: 1, fontSize: '6px', color: '#666' }}>
                    {puzzle.words.find(w => w.row === r && w.col === c).number}
                  </span>
                )}
              </div>
            )))}
          </div>
        ) : (
          <p>Could not generate puzzle. Try simpler words.</p>
        )}
      </div>
    </div>
  );
};

const btnStyle = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' });

export default CrosswordBoard;